// components/RegistrationDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getRegistrationById, updatePaymentStatus } from '../services/api';
import { logout } from '../services/auth';
import {
    Button, Box, Typography, Paper, Table, TableBody,
    TableCell, TableContainer, TableRow, IconButton,
    Grid, Card, CardMedia, Chip, Dialog, DialogTitle,
    DialogContent, DialogActions, Select, MenuItem,
    FormControl, InputLabel, Alert
} from '@mui/material';
import { ArrowBack, Logout, CheckCircle, Cancel, HourglassEmpty, Payment, Download } from '@mui/icons-material';

// Marathi translations
const translations = {
    registrationDetails: 'नोंदणीचे तपशील',
    back: 'मागे',
    logout: 'बाहेर पडणे',
    passportPhoto: 'पासपोर्ट फोटो',
    fullPhoto: 'पूर्ण फोटो',
    signature: 'सही',
    upiScreenshot: 'UPI पेमेंट स्क्रीनशॉट',
    paymentStatus: 'पेमेंट स्थिती',
    updatePayment: 'पेमेंट स्थिती बदला',
    pending: 'प्रलंबित',
    pending_verification: 'तपासणी प्रलंबित',
    paid: 'पेमेंट पूर्ण',
    rejected: 'नाकारले',
};

const getPaymentStatusColor = (status) => {
    switch (status) {
        case 'paid': return 'success';
        case 'pending_verification': return 'warning';
        case 'rejected': return 'error';
        default: return 'default';
    }
};

const getPaymentStatusIcon = (status) => {
    switch (status) {
        case 'paid': return <CheckCircle />;
        case 'pending_verification': return <HourglassEmpty />;
        case 'rejected': return <Cancel />;
        default: return <Payment />;
    }
};

const getPaymentStatusLabel = (status) => {
    switch (status) {
        case 'paid': return translations.paid;
        case 'pending_verification': return translations.pending_verification;
        case 'rejected': return translations.rejected;
        default: return translations.pending;
    }
};

const fieldTranslations = {
    id: 'आयडी',
    sr_no_autogen: 'नोंदणी क्रमांक',
    name: 'नाव',
    mobile_number: 'मोबाईल नंबर',
    dob: 'जन्मतारीख',
    dot: 'जन्मवेळ',
    education: 'शिक्षण',
    occupation: 'व्यवसाय',
    height: 'उंची',
    skin_color: 'वर्ण',
    passport_photo: 'पासपोर्ट फोटो',
    full_photo: 'पूर्ण फोटो',
    dad_name: 'वडिलांचे नाव',
    mom_name: 'आईचे नाव',
    uncle_name: 'मामांचे नाव',
    guardian_phone: 'पालक संपर्क क्रमांक',
    address: 'पत्ता',
    marital_status: 'वैवाहिक स्थिती',
    public_marriage: 'सामुदायिक विवाह',
    sign_photo: 'सहीची फोटो',
    city: 'शहर/गाव',
    pincode_form_level: 'पिन कोड',
    district: 'जिल्हा',
    payment_status: 'पेमेंट स्थिती',
    payment_id: 'पेमेंट आयडी',
    payment_amount: 'पेमेंट रक्कम',
    payment_utr: 'पेमेंट UTR',
    upi_screenshot: 'UPI स्क्रीनशॉट',
    created_at: 'नोंदणीची तारीख',
};

const RegistrationDetail = () => {
    const { id } = useParams();
    const [registration, setRegistration] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [newPaymentStatus, setNewPaymentStatus] = useState('');
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await getRegistrationById(id);
                setRegistration(data);
                setNewPaymentStatus(data.payment_status || 'pending');
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const handlePaymentStatusUpdate = async () => {
        setUpdating(true);
        setError('');
        try {
            const { data } = await updatePaymentStatus(id, newPaymentStatus);
            setRegistration(data);
            setPaymentDialogOpen(false);
        } catch (err) {
            setError('पेमेंट स्थिती अपडेट करण्यात अयशस्वी');
            console.error(err);
        } finally {
            setUpdating(false);
        }
    };

    if (loading) return <Typography>Loading...</Typography>;
    if (!registration) return <Typography>Registration not found</Typography>;

    const renderValue = (value) => {
        if (typeof value === 'boolean') return value ? 'होय' : 'नाही';
        return value || '-';
    };

    const handleDownload = async (url, filename) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error('Download failed:', err);
            // Fallback: open in new tab
            window.open(url, '_blank');
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <IconButton onClick={() => navigate('/registrations')}>
                    <ArrowBack />
                    <Typography variant="body1" sx={{ ml: 1 }}>{translations.back}</Typography>
                </IconButton>
                <Button
                    startIcon={<Logout />}
                    onClick={() => { logout(); navigate('/login'); }}
                >
                    {translations.logout}
                </Button>
            </Box>

            <Typography variant="h4" gutterBottom>{translations.registrationDetails}</Typography>

            {/* Payment Status Card - Prominent Display */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50', border: '2px solid', borderColor: getPaymentStatusColor(registration.payment_status) + '.main' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h6">{translations.paymentStatus}:</Typography>
                            <Chip
                                icon={getPaymentStatusIcon(registration.payment_status)}
                                label={getPaymentStatusLabel(registration.payment_status)}
                                color={getPaymentStatusColor(registration.payment_status)}
                                sx={{ fontSize: '1rem', py: 2.5 }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: { md: 'right' } }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setPaymentDialogOpen(true)}
                        >
                            {translations.updatePayment}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                        <Table>
                            <TableBody>
                                {Object.entries(registration).map(([key, value]) => (
                                    key !== 'passport_photo' && key !== 'full_photo' && key !== 'sign_photo' && key !== 'upi_screenshot' && (
                                        <TableRow key={key}>
                                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '40%' }}>
                                                {fieldTranslations[key] || key}
                                            </TableCell>
                                            <TableCell>
                                                {key === 'payment_status' ? (
                                                    <Chip
                                                        size="small"
                                                        label={getPaymentStatusLabel(value)}
                                                        color={getPaymentStatusColor(value)}
                                                    />
                                                ) : renderValue(value)}
                                            </TableCell>
                                        </TableRow>
                                    )
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>

                <Grid item xs={12} md={4}>
                    {/* Download All Photos Button */}
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<Download />}
                        sx={{ mb: 2 }}
                        onClick={() => {
                            if (registration.passport_photo) handleDownload(registration.passport_photo, `${registration.name}_passport_photo.jpg`);
                            if (registration.full_photo) handleDownload(registration.full_photo, `${registration.name}_full_photo.jpg`);
                            if (registration.sign_photo) handleDownload(registration.sign_photo, `${registration.name}_signature.jpg`);
                            if (registration.upi_screenshot) handleDownload(registration.upi_screenshot, `${registration.name}_upi_screenshot.jpg`);
                        }}
                    >
                        सर्व फोटो डाउनलोड करा
                    </Button>

                    {/* UPI Screenshot - Prominent Display */}
                    {registration.upi_screenshot && (
                        <Card sx={{ mb: 3, border: '2px solid', borderColor: 'warning.main' }}>
                            <Box sx={{ bgcolor: 'warning.light', p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                                    {translations.upiScreenshot}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => handleDownload(registration.upi_screenshot, `${registration.name}_upi_screenshot.jpg`)}
                                    sx={{ color: 'warning.dark' }}
                                >
                                    <Download />
                                </IconButton>
                            </Box>
                            <CardMedia
                                component="img"
                                image={registration.upi_screenshot}
                                alt={translations.upiScreenshot}
                                sx={{ cursor: 'pointer', maxHeight: 300, objectFit: 'contain' }}
                                onClick={() => window.open(registration.upi_screenshot, '_blank')}
                            />
                            <Typography variant="caption" sx={{ p: 1, display: 'block', textAlign: 'center' }}>
                                (मोठे पाहण्यासाठी क्लिक करा)
                            </Typography>
                        </Card>
                    )}

                    {registration.passport_photo && (
                        <Card sx={{ mb: 3 }}>
                            <CardMedia
                                component="img"
                                image={registration.passport_photo}
                                alt={translations.passportPhoto}
                                sx={{ maxHeight: 250, objectFit: 'contain', cursor: 'pointer' }}
                                onClick={() => window.open(registration.passport_photo, '_blank')}
                            />
                            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1">
                                    {translations.passportPhoto}
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<Download />}
                                    onClick={() => handleDownload(registration.passport_photo, `${registration.name}_passport_photo.jpg`)}
                                >
                                    Download
                                </Button>
                            </Box>
                        </Card>
                    )}

                    {registration.full_photo && (
                        <Card sx={{ mb: 3 }}>
                            <CardMedia
                                component="img"
                                image={registration.full_photo}
                                alt={translations.fullPhoto}
                                sx={{ maxHeight: 300, objectFit: 'contain', cursor: 'pointer' }}
                                onClick={() => window.open(registration.full_photo, '_blank')}
                            />
                            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1">
                                    {translations.fullPhoto}
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<Download />}
                                    onClick={() => handleDownload(registration.full_photo, `${registration.name}_full_photo.jpg`)}
                                >
                                    Download
                                </Button>
                            </Box>
                        </Card>
                    )}

                    {registration.sign_photo && (
                        <Card sx={{ mb: 3 }}>
                            <CardMedia
                                component="img"
                                image={registration.sign_photo}
                                alt={translations.signature}
                                sx={{ maxHeight: 200, objectFit: 'contain', cursor: 'pointer' }}
                                onClick={() => window.open(registration.sign_photo, '_blank')}
                            />
                            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1">
                                    {translations.signature}
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<Download />}
                                    onClick={() => handleDownload(registration.sign_photo, `${registration.name}_signature.jpg`)}
                                >
                                    Download
                                </Button>
                            </Box>
                        </Card>
                    )}
                </Grid>
            </Grid>

            {/* Payment Status Update Dialog */}
            <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)}>
                <DialogTitle>{translations.updatePayment}</DialogTitle>
                <DialogContent sx={{ minWidth: 300 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>{translations.paymentStatus}</InputLabel>
                        <Select
                            value={newPaymentStatus}
                            label={translations.paymentStatus}
                            onChange={(e) => setNewPaymentStatus(e.target.value)}
                        >
                            <MenuItem value="pending">{translations.pending}</MenuItem>
                            <MenuItem value="pending_verification">{translations.pending_verification}</MenuItem>
                            <MenuItem value="paid">{translations.paid}</MenuItem>
                            <MenuItem value="rejected">{translations.rejected}</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaymentDialogOpen(false)}>{translations.back}</Button>
                    <Button
                        onClick={handlePaymentStatusUpdate}
                        variant="contained"
                        disabled={updating}
                    >
                        {updating ? 'अपडेट करत आहे...' : 'अपडेट करा'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RegistrationDetail;